<template>
  <div class="shape-matching-game">
    <LevelComplete v-if="isGameComplete" />

    <div v-else>
      <!-- Target Shape -->
      <div
        class="shape-target"
        :class="`${currentShape.name.toLowerCase()}-target`"
        :style="getTargetShapeStyle(currentShape.name)"
        @dragover.prevent
        @drop="handleDrop"
        @click="speakOutLoud(currentShape.name)"
      >
        <h2 class="shape-label">
          {{ currentShape.name }}
        </h2>
      </div>

      <!-- Messages -->
      <p v-if="message" class="message">
        {{ message }}
      </p>

      <!-- Draggable Emojis -->
      <div class="draggable-items">
        <div
          v-for="item in draggableItems"
          :key="item.emoji"
          class="draggable-emoji"
          :style="draggedItem === item ? draggedStyle : {}"
          draggable="true"
          @dragstart="handleDrag($event, item)"
          @click="speakOutLoud(item.emoji)"
          @touchstart="handleTouchStart($event, item)"
          @touchmove="handleTouchMove($event)"
          @touchend="handleTouchEnd($event)"
        >
          {{ item.emoji }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    const shapes = [
      {
        name: "Triangle",
        correctEmoji: "🍕",
        options: ["🍕", "🚗", "🏠", "🌳"],
      },
      {
        name: "Circle",
        correctEmoji: "🎈",
        options: ["🎈", "📦", "💻", "🍎"],
      },
      {
        name: "Square",
        correctEmoji: "📦",
        options: ["📦", "🚀", "🔺", "🐶"],
      },
      {
        name: "Rectangle",
        correctEmoji: "🖥️",
        options: ["🖥️", "🏀", "🔵", "📕"],
      },
      {
        name: "Pentagon",
        correctEmoji: "🏠",
        options: ["🏠", "💎", "🚒", "📘"],
      },
    ];

    return {
      shapes,
      currentShapeIndex: 0,
      currentShape: shapes[0],
      draggableItems: [],
      message: "",
      draggedItem: null,
      draggedStyle: {},
      selectedVoice: null,
      isGameComplete: false,
      voiceSetupCompleted: false,
      timeouts: [],
    };
  },
  mounted() {
    this.setupDraggableItems();

    if (window.speechSynthesis) {
      this.setupVoices();

      if (!this.voiceSetupCompleted) {
        if (typeof window.speechSynthesis.addEventListener === "function") {
          window.speechSynthesis.addEventListener(
            "voiceschanged",
            this.setupVoices
          );
        } else {
          window.speechSynthesis.onvoiceschanged = this.setupVoices;
        }
        this.voiceSetupCompleted = true;
      }
    }
  },
  beforeUnmount() {
    this.timeouts.forEach((id) => clearTimeout(id));
    this.timeouts = [];

    if (
      window.speechSynthesis &&
      typeof window.speechSynthesis.removeEventListener === "function"
    ) {
      window.speechSynthesis.removeEventListener(
        "voiceschanged",
        this.setupVoices
      );
    }
  },
  methods: {
    setSafeTimeout(callback, delay) {
      const timeoutId = setTimeout(() => {
        this.timeouts = this.timeouts.filter((id) => id !== timeoutId);
        callback();
      }, delay);

      this.timeouts.push(timeoutId);
    },

    shuffleArray(array) {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const randomIndex = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[randomIndex]] = [
          shuffled[randomIndex],
          shuffled[i],
        ];
      }
      return shuffled;
    },

    setupDraggableItems() {
      if (!this.currentShape || !Array.isArray(this.currentShape.options)) {
        this.draggableItems = [];
        return;
      }

      const options = this.shuffleArray(this.currentShape.options);
      this.draggableItems = options.map((emoji) => ({ emoji }));
    },

    handleDrag(event, item) {
      if (!event.dataTransfer) return;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", JSON.stringify(item));
    },

    handleDrop(event) {
      if (this.isGameComplete) return;

      let item = null;

      try {
        const itemData = event?.dataTransfer?.getData("text/plain");
        if (!itemData) return;
        item = JSON.parse(itemData);
      } catch (error) {
        console.error("Invalid drag data:", error);
        return;
      }

      if (!item || !item.emoji) return;

      if (item.emoji === this.currentShape.correctEmoji) {
        this.message = "✅";
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );

        this.setSafeTimeout(() => {
          this.message = "";
          this.nextShape();
        }, 1000);
      } else {
        this.message = "❌";

        this.setSafeTimeout(() => {
          this.message = "";
        }, 1000);
      }
    },

    nextShape() {
      this.currentShapeIndex += 1;

      if (this.currentShapeIndex < this.shapes.length) {
        this.currentShape = this.shapes[this.currentShapeIndex];
        this.setupDraggableItems();
        this.draggedItem = null;
        this.draggedStyle = {};
        return;
      }

      this.isGameComplete = true;
      this.message = "";
      this.draggableItems = [];
      this.draggedItem = null;
      this.draggedStyle = {};

      this.$nextTick(() => {
        this.speakOutLoud("Great job!");
      });

      this.setSafeTimeout(() => {
        if (this.$router) {
          this.$router.push("/shapes");
        }
      }, 5000);
    },

    handleTouchStart(event, item) {
      if (!event.touches || !event.touches.length || this.isGameComplete) return;

      event.preventDefault();
      this.draggedItem = item;
      this.draggedStyle = {
        position: "fixed",
        left: `${event.touches[0].clientX}px`,
        top: `${event.touches[0].clientY}px`,
        transform: "translate(-50%, -50%)",
        zIndex: 1000,
        pointerEvents: "none",
      };
    },

    handleTouchMove(event) {
      if (!this.draggedItem || !event.touches || !event.touches.length) return;

      event.preventDefault();
      this.draggedStyle = {
        ...this.draggedStyle,
        left: `${event.touches[0].clientX}px`,
        top: `${event.touches[0].clientY}px`,
      };
    },

    handleTouchEnd(event) {
      if (!this.draggedItem || !event.changedTouches || !event.changedTouches.length) {
        this.draggedItem = null;
        this.draggedStyle = {};
        return;
      }

      const dropZone = this.$el.querySelector(".shape-target");

      if (!dropZone) {
        this.draggedItem = null;
        this.draggedStyle = {};
        return;
      }

      const rect = dropZone.getBoundingClientRect();
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;

      const isInsideDropZone =
        touchEndX >= rect.left &&
        touchEndX <= rect.right &&
        touchEndY >= rect.top &&
        touchEndY <= rect.bottom;

      if (isInsideDropZone) {
        this.handleDrop({
          dataTransfer: {
            getData: () => JSON.stringify(this.draggedItem),
          },
        });
      } else {
        this.message = "❌";
        this.setSafeTimeout(() => {
          this.message = "";
        }, 1000);
      }

      this.draggedItem = null;
      this.draggedStyle = {};
    },

    playSound(url) {
      try {
        const audio = new Audio(url);
        const playPromise = audio.play();

        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch((error) => {
            console.warn("Audio playback failed:", error);
          });
        }
      } catch (error) {
        console.warn("Unable to play sound:", error);
      }
    },

    getTargetShapeStyle(shapeName) {
      const style = {};

      if (shapeName === "Circle") {
        style.borderRadius = "50%";
      } else if (shapeName === "Rectangle") {
        style.width = "150px";
      } else if (shapeName === "Pentagon") {
        style.width = "100px";
        style.height = "100px";
        style.clipPath =
          "polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%)";
      } else if (shapeName === "Triangle") {
        style.width = "0";
        style.height = "0";
        style.borderLeft = "50px solid transparent";
        style.borderRight = "50px solid transparent";
        style.borderBottom = "100px solid lightgray";
        style.backgroundColor = "transparent";
      }

      return style;
    },
  },
};
</script>

<style scoped>
.shape-matching-game {
  position: relative;
  width: 100%;
  min-height: 100vh;
}

/* Target Shape */
.shape-target {
  width: 100px;
  height: 100px;
  background-color: lightgray;
  border-radius: 10px;
  position: relative;
  text-align: center;
  padding-top: 10px;
  margin: 20px auto;
  cursor: pointer;
}

.shape-target .shape-label {
  position: absolute;
  width: 100%;
  text-align: center;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1rem;
  font-weight: bold;
  color: black;
  text-transform: capitalize;
}

/* Triangle shape target */
.triangle-target {
  background-color: transparent;
  border-left: 50px solid transparent;
  border-right: 50px solid transparent;
  border-bottom: 100px solid lightgray;
  width: 0 !important;
  height: 0 !important;
  position: relative;
}

.triangle-target .shape-label {
  top: -25px;
  left: -50px;
  transform: translateX(0);
  width: 100px;
}

/* Pentagon shape target */
.pentagon-target {
  clip-path: polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%);
  width: 100px;
  height: 100px;
  position: relative;
}

/* Rectangle shape target */
.rectangle-target {
  width: 150px;
}

/* Messages */
.message {
  text-align: center;
  font-size: 2rem;
  font-weight: bold;
  margin: 20px 0;
}

/* Draggable items */
.draggable-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  align-items: center;
  margin: auto;
  width: 80%;
  padding: 20px 0;
}

.draggable-emoji {
  cursor: grab;
  font-size: 5rem;
  transition: transform 0.3s;
  margin: 10px;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
}

.draggable-emoji:hover {
  transform: scale(1.2);
}

@media (hover: none) {
  .draggable-emoji:hover {
    transform: none;
  }
}
</style>